import React, { useEffect, useState } from "react";
import useFontFamilies from "../../../hooks/useFontFamilies";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Tabs,
  Tab,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Modal,
  TextField,
  IconButton,
  Slider,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import UseToast from "../../UseToast";
import { MuiColorInput } from "mui-color-input";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import WeatherCustomizationModal from "./WeatherCustomizationModal";
import CaptionSettingsModal from "../../CaptionSettingsModel";
import WheelchairCustomizationModal from "./WheelchairCustomizationModal";

// Styled components for better UI
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  borderRadius: "12px",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  fontWeight: 600,
  borderBottom: `2px solid ${theme.palette.primary.light}`,
  paddingBottom: theme.spacing(1),
}));

const DayScheduleCard = styled(Paper)(({ theme, isenabled }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${isenabled ? theme.palette.primary.light : "#e0e0e0"}`,
  backgroundColor: isenabled ? "#f8fdff" : "#fafafa",
  transition: "all 0.3s ease",
  borderRadius: "8px",
}));

const TimeInputContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

// Fixed Custom styled switch to ensure visibility
const CustomSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
      },
    },
    "&.Mui-disabled": {
      color: theme.palette.grey[400],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.5,
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: "currentColor",
  },
  "& .MuiSwitch-track": {
    backgroundColor: theme.palette.grey[400],
    opacity: 1,
  },
}));

// Timezone data - you can expand this list as needed
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "UTC", label: "Coordinated Universal Time (UTC)" },
  { value: "Europe/London", label: "Greenwich Mean Time (GMT)" },
  { value: "Europe/Paris", label: "Central European Time (CET)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "Australia/Sydney", label: "Australian Eastern Time (AET)" },
];

const CaptionModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const CaptionModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "12px",
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  width: "90%",
  maxWidth: "900px",
  maxHeight: "90vh",
  overflowY: "auto",
  position: "relative",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const CaptionFieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  backgroundColor: "#fafafa",
}));

const CaptionPreview = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#1a1a1a",
  color: "#fff",
  borderRadius: "8px",
  marginTop: theme.spacing(2),
  minHeight: "150px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
}));

const PreviewVideoMock = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  background: "linear-gradient(45deg, #2c3e50 0%, #4a6491 100%)",
  position: "absolute",
  top: 0,
  left: 0,
  opacity: 0.7,
}));

const PreviewText = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  maxWidth: "80%",
  transition: "all 0.3s ease",
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const ShadowInputsContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

export default function AvailabilitySettings() {
  const fontOptions = useFontFamilies('option');
  const { userId } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const { showMsgSuccessToast, showErrorToast } = UseToast();

  // State for active tab
  const [activeTab, setActiveTab] = useState(0);

  // State for additional fields
  const [client_url, setAiHumanUrl] = useState("");
  const [menu_client_url, setMenuUrl] = useState("");
  const [bypass_menu_login, setBypassLogin] = useState(false);
  const [skipAttractLoop, setSkipAttractLoop] = useState(false);
  const [preventPreloadScreen, setPreventPreloadScreen] = useState(false);
  const [removeWhiteFlashOnVideo, setRemoveWhiteFlashOnVideo] = useState(false);
  const [brandColor, setBrandColor] = useState("#000000");
  const [brandFont, setBrandFont] = useState("Arial");
  const [lineHeight, setLineHeight] = useState(0.6);
  const [padding, setPadding] = useState(10);
  const [fontSize, setFontSize] = useState(66);
  const [menuPopup, setMenuPopup] = useState(60);
  const [redirectAttactLoop, setRedirectAttactLoop] = useState(false);

  // New timezone state
  const [timezone, setTimezone] = useState("America/New_York");
  const [useTimezone, setUseTimezone] = useState(true);

  // New fields for monthly open ai total token and live streaming mint
  const [monthlyOpenAiTotalToken, setMonthlyOpenAiTotalToken] = useState(0);
  const [liveStreamingMint, setLiveStreamingMint] = useState(0);

  const [
    enableAttractLoopSwitchOnMainMenu,
    setEnableAttractLoopSwitchOnMainMenu,
  ] = useState(false);

  const [disableAreYouTherePopup, setDisableAreYouTherePopup] = useState(false);

  // New state for invisible button toggle feature
  const [enableInvisibleToggleButton, setEnableInvisibleToggleButton] = useState(false);

  // New state for disable local video download
  const [disableLocalVideoDownload, setDisableLocalVideoDownload] = useState(false);

  // Feature access state
  const [featureAccess, setFeatureAccess] = useState({
    aihEnabled: true,
    menuQuizEnabled: true,
  });

  // Enhanced weekly timing state with enable/disable functionality
  const [weeklyTiming, setWeeklyTiming] = useState([
    {
      day: "Monday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
    {
      day: "Tuesday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
    {
      day: "Wednesday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
    {
      day: "Thursday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
    {
      day: "Friday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
    {
      day: "Saturday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
    {
      day: "Sunday",
      enabled: true,
      isOpen: true,
      isFullDay: true,
      startTime: "00:00",
      endTime: "00:00",
    },
  ]);

  // New state for caption settings modal
  const [captionModalOpen, setCaptionModalOpen] = useState(false);
  const [hasCaptionSettings, setHasCaptionSettings] = useState(false);

  // Weather modal state
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [hasWeatherSettings, setHasWeatherSettings] = useState(false);
  const [weatherEnabled, setWeatherEnabled] = useState(false);

  // Wheelchair modal state
  const [wheelchairModalOpen, setWheelchairModalOpen] = useState(false);
  const [hasWheelchairSettings, setHasWheelchairSettings] = useState(false);
  const [wheelchairEnabled, setWheelchairEnabled] = useState(false);

  // Fetch weather enabled status for this user
  useEffect(() => {
    if (userId) {
      fetch(`/api/weather-settings/get/${userId}`)
        .then(r => r.json())
        .then(data => { if (data?.enabled) setWeatherEnabled(true); })
        .catch(() => { });
    }
  }, [userId]);

  // Fetch wheelchair enabled status for this user
  useEffect(() => {
    if (userId) {
      fetch(`/api/wheelchair-settings/get/${userId}`)
        .then(r => r.json())
        .then(data => {
          if (data?.success && data?.data?.enabled) {
            setWheelchairEnabled(true);
            setHasWheelchairSettings(true);
          }
        })
        .catch(() => { });
    }
  }, [userId]);

  // Determine which tabs to show based on feature access
  const shouldShowMenuSettingsTab = featureAccess?.menuQuizEnabled;

  // Calculate available tabs based on feature access
  const availableTabs = [
    ...(shouldShowMenuSettingsTab
      ? [
        { label: "Menu Settings", path: `/view-button-settings/${userId}` },
        { label: "Splash Settings", path: `/view-splash-settings/${userId}` },
        { label: "Global Settings", path: `/global-settings/${userId}` },
      ]
      : []),
    { label: "System Settings", path: `/availablity-settings/${userId}` },
  ];

  // Update active tab based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const currentTabIndex = availableTabs.findIndex(
      (tab) => tab.path === currentPath
    );

    if (currentTabIndex !== -1) {
      setActiveTab(currentTabIndex);
    } else {
      // Default to System Settings if current route doesn't match any tab
      const systemSettingsIndex = availableTabs.findIndex(
        (tab) => tab.label === "System Settings"
      );
      setActiveTab(systemSettingsIndex >= 0 ? systemSettingsIndex : 0);
    }
  }, [location.pathname, availableTabs]);

  // Fetch Data - Enhanced version
  useEffect(() => {
    if (userId) {
      // Fetch client settings for feature access
      axios
        .get(`/auth/settings/client/${userId}`)
        .then((response) => {
          const clientSettings = response.data;

          // Set feature access from client settings
          if (clientSettings?.settings?.featureAccess) {
            setFeatureAccess(clientSettings?.settings?.featureAccess);
          }
        })
        .catch((error) => {
          console.error("Failed to load client settings:", error);
          // Use default values if API fails
          setFeatureAccess({
            aihEnabled: true,
            menuQuizEnabled: true,
          });
        });

      // Fetch availability settings
      axios
        .get(`/api/availability/${userId}`)
        .then((response) => {
          const data = response.data;

          console.log("data", data);

          if (data.weeklyTiming) {
            const updatedTiming = data.weeklyTiming.map((day) => {
              // Check if it's truly 24 hours (both times are 00:00)
              const isTwentyFourHours =
                day.startTime === "00:00" && day.endTime === "00:00";

              // Check if times are set to any specific values
              const hasSpecificTimes =
                day.startTime !== "00:00" || day.endTime !== "00:00";

              // Determine isFullDay:
              // 1. Use the value from DB if it exists
              // 2. Otherwise, calculate based on times
              let calculatedIsFullDay;
              if (day.isFullDay !== undefined) {
                calculatedIsFullDay = day.isFullDay;
                console.log("1st executes");
              } else {
                calculatedIsFullDay = isTwentyFourHours && !hasSpecificTimes;
                console.log("2nd executes");
              }

              console.log(
                "calculated Day",
                calculatedIsFullDay,
                isTwentyFourHours,
                !hasSpecificTimes
              );

              return {
                ...day,
                isOpen: day.isOpen,
                isFullDay: calculatedIsFullDay,
                startTime: day.startTime || "00:00",
                endTime: day.endTime || "00:00",
                enabled: day.isOpen !== false,
              };
            });
            setWeeklyTiming(updatedTiming);
          }

          if (data.UserData?.client_url)
            setAiHumanUrl(data.UserData?.client_url);
          if (data.UserData?.menu_client_url)
            setMenuUrl(data.UserData?.menu_client_url);
          if (data.UserData?.bypass_menu_login !== undefined)
            setBypassLogin(data.UserData?.bypass_menu_login);
          if (data.UserData?.brandColor)
            setBrandColor(data.UserData?.brandColor);
          if (data.UserData?.brandFont) setBrandFont(data.UserData?.brandFont);
          if (data.UserData?.lineHeight)
            setLineHeight(data.UserData?.lineHeight);
          if (data.UserData?.padding) setPadding(data.UserData?.padding);
          if (data.UserData?.fontSize) setFontSize(data.UserData?.fontSize);
          if (data.skipAttractLoop) setSkipAttractLoop(data.skipAttractLoop);
          if (data.preventPreloadScreen)
            setPreventPreloadScreen(data.preventPreloadScreen);
          if (data.removeWhiteFlashOnVideo)
            setRemoveWhiteFlashOnVideo(data.removeWhiteFlashOnVideo);
          if (data.menuPopup) setMenuPopup(data.menuPopup);
          if (data.redirectAttactLoop)
            setRedirectAttactLoop(data.redirectAttactLoop);

          // Set timezone data if available
          if (data.timezone) setTimezone(data.timezone);
          if (data.useTimezone !== undefined) setUseTimezone(data.useTimezone);

          // Set new fields if available
          if (data.monthlyOpenAiTotalToken !== undefined)
            setMonthlyOpenAiTotalToken(data.monthlyOpenAiTotalToken);
          if (data.liveStreamingMint !== undefined)
            setLiveStreamingMint(data.liveStreamingMint);

          if (data.enableAttractLoopSwitchOnMainMenu !== undefined) {
            setEnableAttractLoopSwitchOnMainMenu(
              data.enableAttractLoopSwitchOnMainMenu
            );
          }

          if (data.disableAreYouTherePopup !== undefined) {
            setDisableAreYouTherePopup(data.disableAreYouTherePopup);
          }

          if (data.enableInvisibleToggleButton !== undefined) {
            setEnableInvisibleToggleButton(data.enableInvisibleToggleButton);
          }

          if (data.disableLocalVideoDownload !== undefined) {
            setDisableLocalVideoDownload(data.disableLocalVideoDownload);
          }
        })
        .catch(() => showErrorToast("Failed to load availability settings"));
    }
  }, [userId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(availableTabs[newValue].path);
  };

  // Handle Weekly Timing Input Changes
  const handleInputChange = (index, field, value) => {
    setWeeklyTiming((prev) => {
      const updatedTiming = [...prev];
      updatedTiming[index][field] = value;

      // If user enters specific times (startTime or endTime), automatically turn off isFullDay
      if ((field === "startTime" || field === "endTime") && value !== "00:00") {
        updatedTiming[index].isFullDay = false;
      }

      // If both times are set back to 00:00, automatically turn on isFullDay
      if (
        field === "startTime" &&
        value === "00:00" &&
        updatedTiming[index].endTime === "00:00"
      ) {
        updatedTiming[index].isFullDay = true;
      }
      if (
        field === "endTime" &&
        value === "00:00" &&
        updatedTiming[index].startTime === "00:00"
      ) {
        updatedTiming[index].isFullDay = true;
      }

      // If enabling/disabling a day, update isOpen accordingly
      if (field === "enabled") {
        updatedTiming[index].isOpen = value;

        if (value === false) {
          // If disabling, set isFullDay to false but preserve times
          updatedTiming[index] = {
            ...updatedTiming[index],
            isFullDay: false,
          };
        } else {
          // If enabling, check if we should set isFullDay based on times
          const hasSpecificTimes =
            updatedTiming[index].startTime !== "00:00" ||
            updatedTiming[index].endTime !== "00:00";
          updatedTiming[index].isFullDay = !hasSpecificTimes;
        }
      }

      // If isOpen is set to false, set isFullDay to false
      if (field === "isOpen" && value === false) {
        updatedTiming[index].isFullDay = false;
      }

      // If isOpen is set to true and isFullDay is not set, determine based on times
      if (
        field === "isOpen" &&
        value === true &&
        updatedTiming[index].isFullDay === undefined
      ) {
        const hasSpecificTimes =
          updatedTiming[index].startTime !== "00:00" ||
          updatedTiming[index].endTime !== "00:00";
        updatedTiming[index].isFullDay = !hasSpecificTimes;
      }

      return updatedTiming;
    });
  };

  // Handle 24 hours toggle change - ALWAYS preserve time values
  const handleFullDayChange = (index, checked) => {
    setWeeklyTiming((prev) => {
      const updated = [...prev];
      const currentDay = updated[index];

      updated[index] = {
        ...currentDay,
        isFullDay: checked,
        // NEVER reset times - always preserve whatever the user entered
        // The times will remain as they were and will be used when isFullDay is false
      };

      return updated;
    });
  };

  // Handle enable/disable all days
  const handleToggleAllDays = (enable) => {
    setWeeklyTiming((prev) =>
      prev.map((day) => {
        const hasSpecificTimes =
          day.startTime !== "00:00" || day.endTime !== "00:00";

        return {
          ...day,
          enabled: enable,
          isOpen: enable,
          // If enabling, set isFullDay based on whether specific times exist
          // If disabling, set isFullDay to false
          isFullDay: enable ? !hasSpecificTimes : false,
        };
      })
    );
  };

  // Handle Form Submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      userId,
      weeklyTiming,
      client_url,
      menu_client_url,
      bypass_menu_login,
      brandColor,
      brandFont,
      lineHeight,
      padding,
      fontSize,
      skipAttractLoop,
      preventPreloadScreen,
      removeWhiteFlashOnVideo,
      menuPopup,
      redirectAttactLoop,
      timezone,
      useTimezone,

      enableAttractLoopSwitchOnMainMenu,
      disableAreYouTherePopup,
      enableInvisibleToggleButton,
      disableLocalVideoDownload,
    };

    axios
      .post(`/api/availability`, payload)
      .then((response) => {
        showMsgSuccessToast(response.data.message);
      })
      .catch((error) => {
        if (error.response) {
          showErrorToast(
            error.response.data.message || "Failed to update availability"
          );
        } else {
          showErrorToast("Network error, please try again");
        }
      });
  };

  // Determine which URL inputs to show based on feature access
  const shouldShowAiHumanInput = featureAccess.aihEnabled;
  const shouldShowMenuInput = featureAccess.menuQuizEnabled;

  const handleOpenCaptionModal = () => {
    setCaptionModalOpen(true);
  };

  const handleCloseCaptionModal = () => {
    setCaptionModalOpen(false);
  };

  const handleOpenWeatherModal = () => {
    setWeatherModalOpen(true);
  };

  const handleCloseWeatherModal = () => {
    setWeatherModalOpen(false);
  };

  const handleOpenWheelchairModal = () => {
    setWheelchairModalOpen(true);
  };

  const handleCloseWheelchairModal = () => {
    setWheelchairModalOpen(false);
  };

  return (
    <div className="main-container">
      <div className="pd-20 xs-pd-20-10">
        <div className="min-height-200px dashboard-container">
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <h4>System Settings</h4>
              </div>
              <div className="col-md-6 col-sm-12 text-end">
                <button
                  className="btn btn-outline-secondary btn-back-effect"
                  onClick={() => navigate(-1)}
                  style={{
                    "--brand-color": brandColor,
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>
              </div>
            </div>
          </div>

          <Box sx={{ width: "100%" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                marginBottom: "36px",
                "& .MuiTab-root": {
                  fontWeight: 500,
                  fontSize: "14px",
                  "&:hover": {
                    color: "#fff",
                  },
                },
              }}
            >
              {availableTabs.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  sx={{
                    "&:hover": {
                      backgroundColor: brandColor,
                      color: "#fff",
                      fontWeight: "bold",
                    },
                  }}
                />
              ))}
            </Tabs>

            <form onSubmit={handleSubmit}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">URL Configuration</SectionTitle>
                  <Grid container spacing={3}>
                    {/* AI Human URL Input - Conditionally Rendered */}
                    {shouldShowAiHumanInput && (
                      <Grid item xs={12} md={6}>
                        <div className="form-group">
                          <label
                            style={{
                              fontWeight: 600,
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            Enter AI Human URL:
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={client_url}
                            onChange={(e) => setAiHumanUrl(e.target.value)}
                            style={{ borderRadius: "6px", padding: "10px" }}
                          />
                        </div>
                      </Grid>
                    )}

                    {/* Menu URL Input - Conditionally Rendered */}
                    {shouldShowMenuInput && (
                      <Grid item xs={12} md={6}>
                        <div className="form-group">
                          <label
                            style={{
                              fontWeight: 600,
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            Enter Menu URL:
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={menu_client_url}
                            onChange={(e) => setMenuUrl(e.target.value)}
                            style={{ borderRadius: "6px", padding: "10px" }}
                          />
                        </div>
                      </Grid>
                    )}

                    {/* Show message if no features are enabled */}
                    {!shouldShowAiHumanInput && !shouldShowMenuInput && (
                      <Grid item xs={12}>
                        <div
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "6px",
                            border: "1px dashed #dee2e6",
                          }}
                        >
                          <Typography variant="body1" color="textSecondary">
                            No URL configuration available. Both AI Human and
                            Menu features are disabled.
                          </Typography>
                        </div>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <CustomSwitch
                            checked={bypass_menu_login}
                            onChange={(e) => setBypassLogin(e.target.checked)}
                          />
                        }
                        label="Bypass Login"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>

              {hasCaptionSettings && (
                <StyledCard sx={{ mt: 3 }}>
                  <CardContent>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <SectionTitle variant="h6">
                        Video Caption Settings
                      </SectionTitle>
                      <Button
                        variant="outlined"
                        onClick={handleOpenCaptionModal}
                        startIcon={<FormatSizeIcon />}
                        style={{
                          border: `1px solid ${brandColor}`,
                        }}
                        sx={{
                          borderColor: brandColor,
                          color: brandColor,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: 600,
                          border: `1px solid ${brandColor}`,
                          "&:hover": {
                            backgroundColor: brandColor,
                            color: "white",
                            borderColor: brandColor,
                          },
                        }}
                      >
                        {hasCaptionSettings
                          ? "Edit Caption Settings"
                          : "Configure Caption Settings"}
                      </Button>
                    </div>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1, mb: 2 }}
                    >
                      Customize the appearance of video captions with colors,
                      fonts, and spacing.
                      {hasCaptionSettings &&
                        " Custom settings are currently active."}
                    </Typography>
                  </CardContent>
                </StyledCard>
              )}

              {/* Weather Widget Customization Card */}
              {weatherEnabled && (
                <StyledCard sx={{ mt: 3 }}>
                  <CardContent>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <SectionTitle variant="h6">🌤 Weather Widget Settings</SectionTitle>
                      <Button
                        variant="outlined"
                        onClick={handleOpenWeatherModal}
                        style={{
                          border: `1px solid ${brandColor}`,
                        }}
                        sx={{
                          borderColor: brandColor,
                          color: brandColor,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: 600,
                          border: `1px solid ${brandColor}`,
                          "&:hover": {
                            backgroundColor: brandColor,
                            color: "white",
                            borderColor: brandColor,
                          },
                        }}
                      >
                        Customize Weather Widget
                      </Button>
                    </div>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                      Configure the weather widget appearance, position, forecast days, temperature units, and more.
                    </Typography>
                  </CardContent>
                </StyledCard>
              )}

              {/* Wheelchair Accessibility Card */}
              <StyledCard sx={{ mt: 3 }}>
                <CardContent>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <SectionTitle variant="h6">♿ Wheelchair Accessibility Settings</SectionTitle>
                    <Button
                      variant="outlined"
                      onClick={handleOpenWheelchairModal}
                      style={{
                        border: `1px solid ${brandColor}`,
                      }}
                      sx={{
                        borderColor: brandColor,
                        color: brandColor,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        border: `1px solid ${brandColor}`,
                        "&:hover": {
                          backgroundColor: brandColor,
                          color: "white",
                          borderColor: brandColor,
                        },
                      }}
                    >
                      {hasWheelchairSettings ? 'Edit Wheelchair Settings' : 'Configure Wheelchair Feature'}
                    </Button>
                  </div>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                    Enable wheelchair accessibility mode with a toggle button that adjusts content positioning for better visibility.
                    {hasWheelchairSettings && " Custom settings are currently active."}
                  </Typography>
                </CardContent>
              </StyledCard>


              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <StyledCard>
                    <CardContent>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "16px",
                        }}
                      >
                        <SectionTitle variant="h6">
                          Experience Schedule
                        </SectionTitle>
                        <div>
                          <Button
                            size="small"
                            onClick={() => handleToggleAllDays(true)}
                            style={{
                              marginRight: "8px",
                              textTransform: "none",
                              color: "#6c757d",
                              backgroundColor: "transparent",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = brandColor;
                              e.target.style.color = "#ffffff";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                              e.target.style.color = "#6c757d";
                            }}
                          >
                            Enable All
                          </Button>
                          <Button
                            size="small"
                            onClick={() => handleToggleAllDays(false)}
                            style={{
                              textTransform: "none",
                              color: "#6c757d",
                              backgroundColor: "transparent",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = brandColor;
                              e.target.style.color = "#ffffff";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                              e.target.style.color = "#6c757d";
                            }}
                          >
                            Disable All
                          </Button>
                        </div>
                      </div>

                      {/* Timezone Settings */}
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <CustomSwitch
                              checked={useTimezone}
                              onChange={(e) => setUseTimezone(e.target.checked)}
                            />
                          }
                          label="Use Specific Timezone"
                        />

                        {useTimezone && (
                          <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Timezone</InputLabel>
                            <Select
                              value={timezone}
                              label="Select Timezone"
                              onChange={(e) => setTimezone(e.target.value)}
                              sx={{ borderRadius: "6px" }}
                            >
                              {TIMEZONES.map((tz) => (
                                <MenuItem key={tz.value} value={tz.value}>
                                  {tz.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {useTimezone && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mt: 1 }}
                          >
                            Current time in{" "}
                            {
                              TIMEZONES.find((tz) => tz.value === timezone)
                                ?.label
                            }
                            :{" "}
                            {new Date()
                              .toLocaleString("en-US", {
                                timeZone: timezone,
                                hour12: false,
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                weekday: "long",
                              })
                              .replace(/,/g, " - ")}
                          </Typography>
                        )}
                      </Box>

                      {weeklyTiming.map((day, index) => (
                        <DayScheduleCard key={day.day} isenabled={day.isOpen}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <FormControlLabel
                              control={
                                <CustomSwitch
                                  checked={day.isOpen}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "isOpen",
                                      e.target.checked
                                    )
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body1" fontWeight={600}>
                                  {day.day}
                                </Typography>
                              }
                            />

                            {day.isOpen && (
                              <FormControlLabel
                                control={
                                  <CustomSwitch
                                    checked={day.isFullDay}
                                    onChange={(e) =>
                                      handleFullDayChange(
                                        index,
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label="24 Hours"
                              />
                            )}
                          </div>

                          {day.isOpen && !day.isFullDay && (
                            <TimeInputContainer>
                              <input
                                type="time"
                                value={day.startTime}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "startTime",
                                    e.target.value
                                  )
                                }
                                style={{
                                  padding: "6px",
                                  borderRadius: "4px",
                                  border: "1px solid #ddd",
                                  flex: 1,
                                }}
                              />
                              <span style={{ margin: "0 8px" }}>to</span>
                              <input
                                type="time"
                                value={day.endTime}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "endTime",
                                    e.target.value
                                  )
                                }
                                style={{
                                  padding: "6px",
                                  borderRadius: "4px",
                                  border: "1px solid #ddd",
                                  flex: 1,
                                }}
                              />
                            </TimeInputContainer>
                          )}
                        </DayScheduleCard>
                      ))}
                    </CardContent>
                  </StyledCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledCard>
                    <CardContent>
                      <SectionTitle variant="h6">
                        Brand & Display Settings
                      </SectionTitle>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <div className="form-group">
                            <label
                              style={{
                                fontWeight: 600,
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Brand Color:
                            </label>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <MuiColorInput
                                className="form-control mt-1"
                                format="hex"
                                value={brandColor}
                                onChange={(newHex) =>
                                  setBrandColor(newHex?.toUpperCase?.())
                                }
                                fallbackValue="#000000"
                                isAlphaHidden={true}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "0px !important",
                                    height: 53,
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    height: "36px",
                                  },
                                  "& .MuiColorInput-Button": {
                                    width: "49px !important",
                                    height: "40px !important",
                                  },
                                  "& .MuiInputAdornment-root .MuiBox-root": {
                                    width: 18,
                                    height: 18,
                                  },
                                }}
                              />
                            </div>
                          </div>
                        </Grid>

                        <Grid item xs={12}>
                          <div className="form-group">
                            <label
                              style={{
                                fontWeight: 600,
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Brand Font:
                            </label>
                            <select
                              value={brandFont}
                              onChange={(e) => setBrandFont(e.target.value)}
                              className="form-control"
                              style={{ borderRadius: "6px", padding: "10px" }}
                            >
                              {fontOptions}
                            </select>
                          </div>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <div className="form-group">
                            <label
                              style={{
                                fontWeight: 600,
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Line Height:
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={lineHeight}
                              onChange={(e) =>
                                setLineHeight(Number(e.target.value))
                              }
                              step="0.1"
                              min="0.1"
                              max="3"
                              style={{ borderRadius: "6px", padding: "10px" }}
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <div className="form-group">
                            <label
                              style={{
                                fontWeight: 600,
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Padding (px):
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={padding}
                              onChange={(e) =>
                                setPadding(Number(e.target.value))
                              }
                              min="0"
                              max="200"
                              style={{ borderRadius: "6px", padding: "10px" }}
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12}>
                          <div className="form-group">
                            <label
                              style={{
                                fontWeight: 600,
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Font Size (px):
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={fontSize}
                              onChange={(e) =>
                                setFontSize(Number(e.target.value))
                              }
                              min="8"
                              max="200"
                              style={{ borderRadius: "6px", padding: "10px" }}
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12}>
                          <div className="form-group">
                            <label
                              style={{
                                fontWeight: 600,
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Enter time for Menu Popup (seconds):
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={menuPopup}
                              onChange={(e) => {
                                const value = e.target.value;
                                setMenuPopup(value === "" ? "" : Number(value));
                              }}
                              style={{ borderRadius: "6px", padding: "10px" }}
                            />
                          </div>
                        </Grid>
                      </Grid>

                      <Divider style={{ margin: "20px 0" }} />

                      <SectionTitle
                        variant="h6"
                        style={{ fontSize: "14px", marginBottom: "16px" }}
                      >
                        Video Settings
                      </SectionTitle>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={skipAttractLoop}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  if (isChecked) {
                                    setSkipAttractLoop(true);
                                    setRedirectAttactLoop(false);
                                  } else {
                                    setSkipAttractLoop(false);
                                  }
                                }}
                              />
                            }
                            label="Skip Attract Loop Video"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={redirectAttactLoop}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;

                                  if (isChecked) {
                                    if (skipAttractLoop) {
                                      alert(
                                        "Please disable 'Skip Attract Loop' to enable redirect"
                                      );
                                      return;
                                    }
                                    setRedirectAttactLoop(true);
                                  } else {
                                    setRedirectAttactLoop(false);
                                  }
                                }}
                              />
                            }
                            label="Redirects to Attract loop after video ends (Skip attract loop should be off to enable this)"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={preventPreloadScreen}
                                onChange={(e) =>
                                  setPreventPreloadScreen(e.target.checked)
                                }
                              />
                            }
                            label="Prevent Skipping Preloading Screen"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={removeWhiteFlashOnVideo}
                                onChange={(e) =>
                                  setRemoveWhiteFlashOnVideo(e.target.checked)
                                }
                              />
                            }
                            label="Remove White Flash On Video"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={enableAttractLoopSwitchOnMainMenu}
                                onChange={(e) =>
                                  setEnableAttractLoopSwitchOnMainMenu(
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Enable Main Menu upper right invisible button toggling attract loop on and off"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={disableAreYouTherePopup}
                                disabled={enableInvisibleToggleButton}
                                onChange={(e) =>
                                  setDisableAreYouTherePopup(e.target.checked)
                                }
                              />
                            }
                            label="Disable 'Are You There?' Popup (Video pauses on last frame, menu stays forever)"
                          />
                          {enableInvisibleToggleButton && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              sx={{ display: "block", mt: 0.5, ml: 4 }}
                            >
                              ⚠️ This option is disabled because the Invisible
                              Toggle Button is enabled
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={enableInvisibleToggleButton}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setEnableInvisibleToggleButton(isChecked);

                                  // If enabling invisible button, automatically disable "Are You There" popup
                                  if (isChecked && disableAreYouTherePopup) {
                                    setDisableAreYouTherePopup(false);
                                  }
                                }}
                              />
                            }
                            label="Enable Invisible Button in Upper Left Corner on Main Menu Screen (Toggles: Are You There popup, Splash Video 2, Next Button feature)"
                          />
                          {enableInvisibleToggleButton && (
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ display: "block", mt: 0.5, ml: 4 }}
                            >
                              ℹ️ When enabled, the 'Are You There?' popup will
                              be controlled by the invisible button
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={disableLocalVideoDownload}
                                onChange={(e) =>
                                  setDisableLocalVideoDownload(e.target.checked)
                                }
                              />
                            }
                            label="Disable Local Video Download (Skip video preloading on startup)"
                          />
                          {disableLocalVideoDownload && (
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ display: "block", mt: 0.5, ml: 4 }}
                            >
                              ℹ️ Videos will stream directly without preloading. This may affect playback performance.
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  style={{
                    background: brandColor,
                    color: "#ffffff",
                    borderRadius: "8px",
                    padding: "10px 30px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  Save Settings
                </Button>
              </Box>

              {/* Caption Settings Modal */}

              <CaptionSettingsModal
                open={captionModalOpen}
                onClose={handleCloseCaptionModal}
                setHasCaptionSettings={setHasCaptionSettings}
                userId={userId}
                brandColor={brandColor}
              />

              {/* Weather Customization Modal */}
              <WeatherCustomizationModal
                open={weatherModalOpen}
                onClose={handleCloseWeatherModal}
                userId={userId}
                brandColor={brandColor}
                brandFont={brandFont}
                onSave={(settings) => {
                  setHasWeatherSettings(settings.enabled);
                  setWeatherEnabled(settings.enabled);
                }}
              />

              {/* Wheelchair Customization Modal */}
              <WheelchairCustomizationModal
                open={wheelchairModalOpen}
                onClose={handleCloseWheelchairModal}
                userId={userId}
                brandColor={brandColor}
                onSave={(settings) => {
                  setHasWheelchairSettings(settings.enabled);
                  setWheelchairEnabled(settings.enabled);
                }}
              />
            </form>
          </Box>
        </div>
      </div>
      <style jsx>{`
        .dashboard-container .btn-back-effect {
          border: 1px solid #6c757d !important;
          color: #6c757d !important;
          background-color: transparent !important;
          border-radius: 8px;
        }

        .dashboard-container .btn-back-effect:hover {
          background-color: var(--brand-color) !important;
          color: #fff !important;
          border-color: var(--brand-color) !important;
        }
      `}</style>
    </div>
  );
}
